import React, { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Plus, Trash2, Upload, X } from "lucide-react";
import { useSaveProductImageMutation, useDeleteImageMutation } from "state/api";
import CachedImage from "./CachedImage";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ImageDialog = ({ open, onClose, productId, images }) => {
  const [newImage, setNewImage] = useState(null);
  const [saveImageObject, { isLoading: LoadingSave }] =
    useSaveProductImageMutation();
  const [deleteImage, { isLoading: LoadingDelete }] = useDeleteImageMutation();

  const onDeleteImage = async (id) => {
    await deleteImage({ imageId: id, productId });
    onClose();
    toast.success("Product Image Deleted!", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setNewImage(file);
  };

  const handleAddImage = async () => {
    if (newImage) {
      const { data, error } = await saveImageObject({
        productId: productId,
        image: newImage,
      });
      setNewImage(null);
      if (error) {
        toast.error("Please Select Png Format image!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }

      if (data) {
        toast.success("Image added Successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        onClose();
      }
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed top-0 right-0 z-50 h-full w-[70%] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col"
        role="dialog"
        aria-label="Manage Images"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Manage Images
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Add or remove product images. Click on the delete button to remove
              an image.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 h-8 w-8 p-0"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images != null &&
              images.map((image, index) => (
                <Card key={index} className="relative group">
                  <CardContent className="p-0">
                    <CachedImage
                      src={image.image}
                      alt={`Image ${index + 1}`}
                      className="w-full h-32 rounded-t-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                      <Button
                        variant="destructive"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        onClick={() => onDeleteImage(image._id)}
                        disabled={LoadingDelete}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                accept="image/*"
                id="new-image"
                type="file"
                className="hidden"
                onChange={handleImageChange}
              />
              <label htmlFor="new-image">
                <Button variant="outline" asChild>
                  <span>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Image
                  </span>
                </Button>
              </label>
            </div>

            {newImage && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{newImage.name}</p>
                <Button
                  onClick={handleAddImage}
                  disabled={LoadingSave}
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {LoadingSave ? "Uploading..." : "Upload"}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </aside>
    </>
  );
};

export default ImageDialog;
